import React, { useState } from "react";
import { toast } from "sonner";
import { Sun, Moon, Monitor, Bell, GlobeHemisphereEast, User, PaintBrush } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const BRAND_TONE_OPTIONS = [
  { key: "professional", label: { tr: "Profesyonel",  en: "Professional" }, desc: { tr: "Kurumsal, güven veren, ölçülü. 3-4 kısa paragraf.",     en: "Corporate, trustworthy, measured. 3-4 short paragraphs." } },
  { key: "friendly",     label: { tr: "Samimi",       en: "Friendly"     }, desc: { tr: "Sıcak, günlük konuşma dili, satış baskısı yok.",         en: "Warm, conversational, zero sales pressure."               } },
  { key: "premium",      label: { tr: "Prestijli",    en: "Premium"      }, desc: { tr: "Zarif, üst-segment. Prestij ima edilir, söylenmez.",     en: "Refined, elevated. Prestige is implied, never shouted."   } },
  { key: "direct",       label: { tr: "Direkt",       en: "Direct"       }, desc: { tr: "2-3 çok kısa cümle. Sonunda net bir istek.",             en: "2-3 very short sentences. One clear ask at the end."      } },
  { key: "consultative", label: { tr: "Danışmansal",  en: "Consultative" }, desc: { tr: "Soru-odaklı; önce içgörü, sonra öneri.",                 en: "Question-led; insight before pitch."                      } },
];

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const { user, refreshUser } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const [toneBusy, setToneBusy] = useState(false);
  const [notifs, setNotifs] = useState({ email: true, newLead: true, insight: false, weekly: true });

  const currentTone = user?.brand_tone || "professional";

  const setTone = async (tone) => {
    if (tone === currentTone) return;
    setToneBusy(true);
    try {
      await api.patch("/user/brand-tone", { brand_tone: tone });
      await refreshUser();
      toast.success(locale === "en" ? "Brand tone updated" : "Marka tonu güncellendi");
    } catch {
      toast.error(locale === "en" ? "Could not update brand tone" : "Marka tonu güncellenemedi");
    } finally { setToneBusy(false); }
  };

  const themeOptions = [
    { key: "light", label: locale === "en" ? "Light" : "Aydınlık", icon: Sun },
    { key: "dark", label: locale === "en" ? "Dark" : "Karanlık", icon: Moon },
    { key: "system", label: locale === "en" ? "System" : "Sistem", icon: Monitor },
  ];

  return (
    <div className="p-6 md:p-10 max-w-4xl space-y-8">
      <div>
        <div className="acas-eyebrow mb-2">{locale === "en" ? "Preferences" : "Tercihler"}</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
          {locale === "en" ? "Settings" : "Ayarlar"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {locale === "en"
            ? "Your account, theme and notification preferences."
            : "Hesabınız, tema ve bildirim tercihleriniz."}
        </p>
      </div>

      {/* Profile */}
      <Section title={locale === "en" ? "Profile" : "Profil"} icon={User}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>{locale === "en" ? "Full name" : "Ad Soyad"}</Label>
            <Input value={user?.name || ""} readOnly className="mt-1.5" data-testid="settings-name" />
          </div>
          <div>
            <Label>{locale === "en" ? "Email" : "E-posta"}</Label>
            <Input value={user?.email || ""} readOnly className="mt-1.5 mono" data-testid="settings-email" />
          </div>
        </div>
      </Section>

      {/* Theme */}
      <Section title={locale === "en" ? "Theme" : "Tema"} icon={Sun}>
        <div className="grid grid-cols-3 gap-3">
          {themeOptions.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTheme(key)} data-testid={`settings-theme-${key}`}
                    className={`p-4 rounded-md border text-left ${
                      theme === key ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"
                    }`}>
              <Icon size={18} className={theme === key ? "text-primary" : "text-muted-foreground"} />
              <div className="text-sm font-semibold mt-2">{label}</div>
            </button>
          ))}
        </div>
      </Section>

      {/* Language */}
      <Section title={t("settings.language.label", "Dil")} icon={GlobeHemisphereEast}>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: "tr", label: t("settings.language.tr", "Türkçe") },
            { key: "en", label: t("settings.language.en", "English") },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setLocale(key)}
              data-testid={`settings-lang-${key}`}
              className={`p-4 rounded-md border text-left ${
                locale === key ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"
              }`}
            >
              <div className="text-sm font-semibold">{label}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{key.toUpperCase()}</div>
            </button>
          ))}
        </div>
      </Section>

      {/* Brand Tone */}
      <Section title={locale === "en" ? "Brand Tone" : "Marka Tonu"} icon={PaintBrush}>
        <div className="text-xs text-muted-foreground mb-4">
          {locale === "en"
            ? "Sets the tone for AI-generated outreach drafts. Changing it never affects any other setting."
            : "AI'nın hazırladığı ilk temas taslaklarında kullanılır. Diğer ayarlarınız etkilenmez."}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {BRAND_TONE_OPTIONS.map(({ key, label, desc }) => (
            <button
              key={key}
              onClick={() => setTone(key)}
              disabled={toneBusy}
              data-testid={`settings-brand-tone-${key}`}
              className={`text-left p-4 rounded-md border transition ${
                currentTone === key
                  ? "border-primary bg-primary/[0.05] ring-2 ring-primary/25"
                  : "border-border/60 hover:border-primary/40"
              }`}
            >
              <div className="text-sm font-semibold">{label[locale] || label.tr}</div>
              <div className="text-xs text-muted-foreground mt-1">{desc[locale] || desc.tr}</div>
            </button>
          ))}
        </div>
      </Section>

      {/* Notifications */}
      <Section title={locale === "en" ? "Notifications" : "Bildirim Tercihleri"} icon={Bell}>
        <div className="divide-y divide-border/60">
          {[
            { k: "email",   label: locale === "en" ? "Email notifications" : "E-posta bildirimleri", desc: locale === "en" ? "Get important updates by email" : "Önemli güncellemeler e-posta olarak gelsin" },
            { k: "newLead", label: locale === "en" ? "New opportunities"   : "Yeni fırsat",           desc: locale === "en" ? "Notify me when Next io finds a new lead" : "Next io yeni fırsat keşfettiğinde uyar" },
            { k: "insight", label: locale === "en" ? "AI insight updates"  : "AI analiz güncellemeleri", desc: locale === "en" ? "When new insights are generated" : "Yeni içgörüler oluştuğunda haber ver" },
            { k: "weekly",  label: locale === "en" ? "Weekly summary"      : "Haftalık özet",         desc: locale === "en" ? "Weekly digest every Monday" : "Her Pazartesi haftalık rapor gönder" },
          ].map((n) => (
            <div key={n.k} className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm font-semibold">{n.label}</div>
                <div className="text-xs text-muted-foreground">{n.desc}</div>
              </div>
              <Switch checked={notifs[n.k]} onCheckedChange={(v) => setNotifs(s => ({ ...s, [n.k]: v }))}
                      data-testid={`notif-${n.k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={() => toast.success(locale === "en" ? "Preferences saved" : "Tercihler kaydedildi")}
                  data-testid="save-settings">
            {locale === "en" ? "Save" : "Kaydet"}
          </Button>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card p-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={16} weight="duotone" className="text-muted-foreground" />}
        <h2 className="text-sm font-bold uppercase tracking-wider">{title}</h2>
      </div>
      {children}
    </div>
  );
}

